import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiUsers, FiHeart, FiTrendingUp, FiShield, FiStar } = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiHeart,
      title: "Customer First",
      description: "We prioritize customer satisfaction above everything else, ensuring each customer gets personalized service and the best products for their needs."
    },
    {
      icon: FiShield,
      title: "Quality Assurance",
      description: "Every product we sell comes with quality guarantee and manufacturer warranty, ensuring you get authentic and reliable electronics."
    },
    {
      icon: FiStar,
      title: "Excellence",
      description: "We strive for excellence in everything we do, from product selection to customer service, maintaining the highest standards in the industry."
    },
    {
      icon: FiTrendingUp,
      title: "Innovation",
      description: "We stay updated with the latest technology trends to offer our customers the most advanced and innovative electronic products."
    }
  ];

  const milestones = [
    {
      year: "2014",
      title: "Store Opening",
      description: "Opened our first store in Al Satwa, Dubai with a vision to provide quality electronics to the community."
    },
    {
      year: "2017",
      title: "Expansion",
      description: "Expanded our product range to include the latest smartphones, tablets, and accessories from top international brands."
    },
    {
      year: "2020",
      title: "Digital Presence",
      description: "Enhanced our digital presence and customer service capabilities to serve customers better during challenging times."
    },
    {
      year: "2025",
      title: "Today",
      description: "Continuing to serve Dubai community with the latest technology, competitive prices, and exceptional customer service."
    }
  ];

  const teamStats = [
    { number: "10+", label: "Years of Experience" },
    { number: "5000+", label: "Happy Customers" },
    { number: "50+", label: "Brand Partners" },
    { number: "99%", label: "Customer Satisfaction" }
  ];

  return (
    <div className="w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white py-20 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                About <span className="text-yellow-300">Karoon Phones</span> & Electronics
              </h1>
              <p className="text-xl text-red-100 leading-relaxed">
                For over a decade, we have been Dubai's trusted destination for mobile phones, electronics, and accessories. 
                Our commitment to quality, competitive pricing, and exceptional customer service has made us a preferred choice 
                for thousands of satisfied customers.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://i.ibb.co/gFbg9cYT/image-1.jpg"
                alt="Karoon Phones & Electronics Store"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-2xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 w-full">
            {teamStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-red-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 text-sm md:text-base">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Our Story
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Karoon Phones & Electronics was founded with a simple mission: to provide Dubai residents with access to the latest 
                technology at affordable prices. What started as a small electronics store in Al Satwa has grown into one of the most 
                trusted names in mobile phones and electronics in Dubai.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our journey began over a decade ago when we recognized the need for a reliable electronics retailer that combines 
                quality products with exceptional customer service. Today, we continue to uphold these values while expanding our 
                product range and enhancing our services to meet the evolving needs of our customers.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We take pride in building lasting relationships with our customers, offering not just products but complete solutions 
                that enhance their digital lifestyle. Our team of experienced professionals is dedicated to helping you find the 
                perfect device that matches your needs and budget.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://i.ibb.co/Q3BxmfY7/image-2.jpg"
                alt="Our Electronics Collection"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These fundamental principles guide everything we do and shape our relationship with customers and partners
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 w-full">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From humble beginnings to becoming a trusted name in Dubai's electronics retail industry
            </p>
          </motion.div>

          <div className="relative w-full">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-red-200 h-full hidden md:block"></div>
            
            <div className="space-y-12 w-full">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className={`flex flex-col md:flex-row items-center w-full ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'}`}>
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                      <div className="text-2xl font-bold text-red-600 mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{milestone.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
                    </div>
                  </div>
                  
                  <div className="w-8 h-8 bg-red-600 rounded-full border-4 border-white shadow-lg z-10 my-4 md:my-0 flex-shrink-0"></div>
                  
                  <div className="w-full md:w-5/12"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-red-600 text-white w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 w-full">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <div className="bg-white/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto lg:mx-0">
                <SafeIcon icon={FiAward} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Our Mission</h3>
              <p className="text-red-100 leading-relaxed text-lg">
                To provide our customers with the latest technology products at competitive prices, backed by exceptional 
                customer service and technical support. We aim to make advanced technology accessible to everyone in Dubai 
                while building lasting relationships based on trust and reliability.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center lg:text-left"
            >
              <div className="bg-white/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto lg:mx-0">
                <SafeIcon icon={FiUsers} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Our Vision</h3>
              <p className="text-red-100 leading-relaxed text-lg">
                To become the leading electronics retailer in the UAE, known for our commitment to quality, innovation, 
                and customer satisfaction. We envision a future where we continue to evolve with technology trends while 
                maintaining our core values of integrity, excellence, and community service.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;